import { z } from "zod";
// Source of every classifier datapoint.
export const DeviceDataPointSource = z.enum([
    "lookalike",
    "ai",
    "manual",
    "fact",
]);
// English-only controlled vocabulary. Adding a value here is the only sanctioned
// way to widen what the classifier may emit.
export const GenderEnum = z.enum(["Male", "Female", "Unknown"]);
export const AgeGroupEnum = z.enum([
    "14-22",
    "23-34",
    "35-44",
    "45-54",
    "55-65",
    "65+",
]);
export const IncomeTierEnum = z.enum(["Premium", "Medium+", "MassMarket"]);
export const FamilyStatusEnum = z.enum([
    "Single",
    "Married/Co-viewing",
    "Unknown",
]);
export const KidsDemographicsEnum = z.enum([
    "None",
    "Infants_0-2",
    "Preschoolers_3-5",
    "SchoolKids_6-12",
    "Teens",
]);
export const MaritalStatusEnum = z.enum([
    "Married",
    "Divorced",
    "Single",
    "Unknown",
]);
export const MediaConsumptionEnum = z.enum([
    "Hard_Cord_Cutter",
    "Light_Cord_Cutter",
    "Linear_TV_Addict",
    "Hybrid_Streamer",
    "Sports_Live_Only",
]);
export const MandatoryIntentEnum = z.enum([
    "Foodies",
    "SportsFans",
    "HealthIntenders",
    "Automotive",
]);
// Interests are intentionally free-form strings. The list below is a SUGGESTION
// vocabulary passed to the model — the classifier is free to invent new tags
// (e.g., 'BoardGames', 'StandUp', 'WomensFootball') when the evidence warrants
// it. We only require English, single token, <= 32 chars.
export const InterestSuggestions = [
    "Fishing",
    "Hunting",
    "Esports",
    "Gaming",
    "TeleShopping",
    "Religion",
    "Travel",
    "Cooking",
    "Fitness",
    "Wellness",
    "Cars",
    "Tech",
    "Finance",
    "Parenting",
    "Fashion",
    "Pets",
    "DIY",
    "Music",
    "Movies",
    "Books",
];
const InterestValue = z
    .string()
    .min(1)
    .max(32)
    .regex(/^[A-Za-z][A-Za-z0-9_+-]*$/, {
    message: "Interest must be PascalCase ASCII (letters, digits, _+-).",
});
// Singleton vs multi-valued classification — kept in shared/ so the backend
// repository, the frontend renderer, and the tagging script all agree.
export const SINGLETON_PROPERTIES = [
    "gender",
    "ageGroup",
    "incomeTier",
    "familyStatus",
    "maritalStatus",
    "mediaConsumptionType",
];
export const MULTI_VALUED_PROPERTIES = [
    "kidsDemographics",
    "interest",
    "mandatoryIntent",
];
// Persisted-row shape — exactly what comes back from `deviceDataPoint`.
export const DeviceDataPointSchema = z.object({
    uuid: z.string().uuid(),
    deviceId: z.string(),
    property: z.string(),
    value: z.string(),
    certainty: z.number().min(0).max(1),
    source: DeviceDataPointSource,
    createdAt: z.string(),
});
// Helper builders so the two shapes (with-reasoning for OpenAI; without for the
// API response) stay in sync on the enum side.
const reasoned = (v) => z.object({
    value: v,
    certainty: z.number().min(0).max(1),
    reasoning: z.string(),
});
const scored = (v) => z.object({
    value: v,
    certainty: z.number().min(0).max(1),
    source: DeviceDataPointSource,
});
// Shape requested from OpenAI via Structured Outputs. `maritalStatus` is
// deliberately EXCLUDED — per Task.md it is only computable from external
// questionnaires, not from impression logs. `reasoning` is requested (model
// produces it, script logs it) but NOT persisted in the DB.
export const AiInferableClassificationSchema = z.object({
    gender: reasoned(GenderEnum).nullable(),
    ageGroup: reasoned(AgeGroupEnum).nullable(),
    incomeTier: reasoned(IncomeTierEnum).nullable(),
    familyStatus: reasoned(FamilyStatusEnum).nullable(),
    mediaConsumptionType: reasoned(MediaConsumptionEnum).nullable(),
    kidsDemographics: z.array(reasoned(KidsDemographicsEnum)),
    interests: z.array(reasoned(InterestValue)).min(5).max(10),
    mandatoryIntent: z.array(reasoned(MandatoryIntentEnum)),
});
// Shape returned by GET /devices/:id — `maritalStatus` is nullable and only
// populated via source='fact' or source='manual' ingestion. No `reasoning`.
export const DeviceClassificationSchema = z.object({
    gender: scored(GenderEnum).nullable(),
    ageGroup: scored(AgeGroupEnum).nullable(),
    incomeTier: scored(IncomeTierEnum).nullable(),
    familyStatus: scored(FamilyStatusEnum).nullable(),
    maritalStatus: scored(MaritalStatusEnum).nullable(),
    mediaConsumptionType: scored(MediaConsumptionEnum).nullable(),
    kidsDemographics: z.array(scored(KidsDemographicsEnum)),
    interests: z.array(scored(InterestValue)).max(10),
    mandatoryIntent: z.array(scored(MandatoryIntentEnum)),
});
// Listing-page filter for "device has property X with certainty >= N".
export const DeviceClassificationFilterSchema = z.object({
    property: z.string(),
    value: z.string().optional(),
    minCertainty: z.number().min(0).max(1),
});
// Centralized validators the backend/script can call before insert.
export const validateClassificationValue = (property, value) => {
    switch (property) {
        case "gender":
            return GenderEnum.safeParse(value).success;
        case "ageGroup":
            return AgeGroupEnum.safeParse(value).success;
        case "incomeTier":
            return IncomeTierEnum.safeParse(value).success;
        case "familyStatus":
            return FamilyStatusEnum.safeParse(value).success;
        case "maritalStatus":
            return MaritalStatusEnum.safeParse(value).success;
        case "mediaConsumptionType":
            return MediaConsumptionEnum.safeParse(value).success;
        case "kidsDemographics":
            return KidsDemographicsEnum.safeParse(value).success;
        case "interest":
            return InterestValue.safeParse(value).success;
        case "mandatoryIntent":
            return MandatoryIntentEnum.safeParse(value).success;
        default:
            return false;
    }
};
//# sourceMappingURL=deviceDataPoint.js.map